import Alpine from 'alpinejs';

type AlpineType = typeof Alpine;

const FORMAT_LOCALE = 'de';
const FORMAT_OPTIONS: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: 'EUR',
};

const numberFormatter = Intl.NumberFormat(FORMAT_LOCALE, FORMAT_OPTIONS);

const isNumeric = (n) => !isNaN(parseFloat(n)) && isFinite(n);

export const MoneyInput = (Alpine: AlpineType) => {
    Alpine.directive('money-input', (el: HTMLInputElement, directive, { cleanup, evaluate }) => {
        const setDataField = (field, value) => evaluate(`${directive.expression}.${field} = ${value}`);

        const toCentsValue = (value: string) => {
            if (isNumeric(value)) return parseFloat(value);
            const result = parseFloat(value.replace(/\D/g, ''));
            return isNumeric(result) ? result : 0;
        };

        const format = (value) => {
            return numberFormatter.format(value / 100);
        };

        const setCursorPos = (el, currentPosition, diff) => {
            const lengthWithoutEuros = el.value.length - 2;
            let selectionTarget = currentPosition > lengthWithoutEuros ? lengthWithoutEuros : currentPosition + diff;
            if (selectionTarget > lengthWithoutEuros) {
                selectionTarget = lengthWithoutEuros;
            }
            el.setSelectionRange(selectionTarget, selectionTarget);
        };

        const isInAllowedRange = (el, newValue: number) => {
            const max = parseInt(el.getAttribute('max')) ?? null;
            const min = parseInt(el.getAttribute('min')) ?? null;
            if (max && newValue > max) return false;
            if (min && newValue < min) return false;
            return true;
        };

        const process = (el, add = 0) => {
            const currentPosition = el.selectionStart;
            const lengthBefore = el.value.length;
            const floatValue = toCentsValue(el.value) + add;
            const formattedValue = format(floatValue);
            const lengthAfter = formattedValue.length;
            const isValid = isInAllowedRange(el, floatValue);
            setDataField('isValid', isValid);
            if (isValid) {
                setDataField('amount', floatValue);
            }
            el.value = formattedValue;
            setCursorPos(el, currentPosition, lengthAfter - lengthBefore);
        };
        const handler = () => process(el);
        const pressedKeys = {};
        const keydownHandler = (ev: KeyboardEvent) => {
            pressedKeys[ev.key] = true;
            let multiplier = 1;
            if (pressedKeys['Shift']) multiplier *= 10;
            if (pressedKeys['Control']) multiplier *= 10;
            if (pressedKeys['Alt']) multiplier *= 10;
            if (pressedKeys['ArrowUp']) {
                process(el, (parseInt(el.getAttribute('step')) ?? 100) * multiplier);
                ev.preventDefault();
            }
            if (pressedKeys['ArrowDown']) {
                process(el, -(parseInt(el.getAttribute('step')) ?? 100) * multiplier);
                ev.preventDefault();
            }
        };
        const keyupHandler = (ev: KeyboardEvent) => {
            pressedKeys[ev.key] = false;
        };

        const init = (el) => {
            if (el.value !== '') {
                el.value = format(el.value);
            }

            el.addEventListener('input', handler);
            el.addEventListener('blur', handler);
            el.addEventListener('keyup', keyupHandler);
            el.addEventListener('keydown', keydownHandler);
        };

        init(el);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        cleanup(() => {
            el.removeEventListener('input', handler);
            el.removeEventListener('blur', handler);
            el.removeEventListener('keyup', keyupHandler);
            el.removeEventListener('keydown', keydownHandler);
        });
    });
};

export default MoneyInput;
